import React, { useState, useEffect, useCallback } from 'react'
import { sanitizeHTML } from '../../../../utils/common-utils'
import { Card, CardRecord } from '../Card/Card'
import { DropdownMenu } from './DropdownMenu'
import { CardListProps } from './CardList.type'

export const CardList = (props: CardListProps) => {
  const {
    cardDescription,
    cardList,
    cardLimit,
    buttonLabel,
    dropdownBefore,
    dropdownAfter,
    allLabelValue,
    defaultCardCategory,
    componentSpacing,
    brandExtension,
  } = props
  const determineUniqueCategories = useCallback(() => {
    const uniqueCategories: any[] = []

    cardList?.forEach(card => {
      if (!card.cardCategory || uniqueCategories.includes(card.cardCategory)) {
        return
      }
      uniqueCategories.push(card.cardCategory)
    })
    return [allLabelValue, ...uniqueCategories.sort()]
  }, [allLabelValue, cardList])

  const getInitialCategory = () => {
    // edge case when we override all label value but set "All" as default
    if ((defaultCardCategory === 'All' && allLabelValue) || defaultCardCategory === '') {
      return allLabelValue
    }
    return defaultCardCategory ?? allLabelValue
  }

  const [maxCards, setMaxCards] = useState(Number(cardLimit))
  const [categories, setCategories] = useState(() => determineUniqueCategories())
  const [currentCategory, setCurrentCategory] = useState(() => getInitialCategory())

  useEffect(() => {
    setCategories(determineUniqueCategories())
  }, [cardList, determineUniqueCategories])

  const handleButtonClick = () => {
    let newMaxCards = maxCards + Number(cardLimit)
    if (newMaxCards > (cardList as CardRecord[]).length) {
      newMaxCards = (cardList as CardRecord[]).length
    }
    setMaxCards(newMaxCards)
  }

  const handleCategoryChange = (category: string) => {
    if (category !== currentCategory) {
      setMaxCards(Number(cardLimit))
    }
    setCurrentCategory(category)
  }

  const cardsToDisplay = cardList?.filter(currCard => {
    if (currentCategory === allLabelValue) {
      return true
    }
    return currCard.cardCategory === currentCategory
  })

  return (
    <section className={`card-list-component ${componentSpacing} ${brandExtension}`}>
      <div
        className='u-sizeConstrained u-sm-flexCol u-sm-flexJustifyCenter'
        data-testid={props.testId ?? null}>
        {cardDescription && (
          <div
            className='card-description Content u-marginBottom20'
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(cardDescription) }}
          />
        )}
        {cardsToDisplay && (
          <>
            {categories.length > 1 && (
              <div className='u-flex u-lg-flexJustifyBetween u-lg-flexRow u-flexCol u-sizeFull u-flexAlignItemsBaseline u-posRelative u-marginBottom20'>
                <DropdownMenu
                  items={categories}
                  onChangeHandler={handleCategoryChange}
                  summaryBeforeText={dropdownBefore}
                  summaryAfterText={dropdownAfter}
                  currentItem={currentCategory}
                />
                <p className='u-sm-paddingTop8 u-md-paddingTop8'>{`${cardsToDisplay.length} Result${
                  cardsToDisplay.length !== 1 ? 's' : ''
                }`}</p>
              </div>
            )}
            <div className='card-list-wrapper u-sm-flex u-sm-flexCol u-marginBottom20'>
              {cardsToDisplay.slice(0, maxCards).map(card => (
                <Card key={card.cardId} card={card} />
              ))}
            </div>
            {maxCards < cardsToDisplay.length && (
              <div className='load-more u-flex u-flexJustifyCenter'>
                <button
                  className='Button Button--secondary Button--secondary--action-blue'
                  onClick={handleButtonClick}>
                  {buttonLabel}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
